import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDocs, 
  getDoc,
  onSnapshot,
  query,
  where,
  orderBy,
  serverTimestamp,
  writeBatch
} from 'firebase/firestore';
import { 
  ref, 
  uploadBytes, 
  getDownloadURL, 
  deleteObject 
} from 'firebase/storage';
import { db, storage } from '../firebaseConfig';

// Collection references
const CLIENTS_COLLECTION = 'clients';
const PRODUCTS_COLLECTION = 'products';
const ORDERS_COLLECTION = 'orders';
const CATEGORIES_COLLECTION = 'categories';

// Utility function to generate unique order ID
export const generateOrderId = () => {
  const timestamp = Date.now();
  const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
  return `ORD-${timestamp}-${random}`;
};

// Client Services
export const clientService = {
  // Create a new client
  async createClient(clientData) {
    try {
      const clientWithTimestamp = {
        ...clientData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        totalOrders: 0,
        totalSpent: 0,
        orders: [] // Array of order IDs
      };
      
      const docRef = await addDoc(collection(db, CLIENTS_COLLECTION), clientWithTimestamp);
      return { id: docRef.id, ...clientWithTimestamp };
    } catch (error) {
      console.error('Error creating client:', error);
      throw new Error('Failed to create client: ' + error.message);
    }
  },

  // Update an existing client
  async updateClient(clientId, clientData) {
    try {
      const clientRef = doc(db, CLIENTS_COLLECTION, clientId);
      const updateData = {
        ...clientData,
        updatedAt: serverTimestamp()
      };
      
      await updateDoc(clientRef, updateData);
      return { id: clientId, ...updateData };
    } catch (error) {
      console.error('Error updating client:', error);
      throw new Error('Failed to update client: ' + error.message);
    }
  },

  // Get a single client by ID
  async getClient(clientId) {
    try {
      const clientRef = doc(db, CLIENTS_COLLECTION, clientId);
      const clientSnap = await getDoc(clientRef);
      
      if (clientSnap.exists()) {
        return { id: clientSnap.id, ...clientSnap.data() };
      } else {
        throw new Error('Client not found');
      }
    } catch (error) {
      console.error('Error getting client:', error);
      throw new Error('Failed to get client: ' + error.message);
    }
  },

  // Get all clients
  async getAllClients() {
    try {
      const querySnapshot = await getDocs(collection(db, CLIENTS_COLLECTION));
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting clients:', error);
      throw new Error('Failed to get clients: ' + error.message);
    }
  },

  // Find client by phone number
  async findClientByPhone(phoneNumber) {
    try {
      const q = query(
        collection(db, CLIENTS_COLLECTION), 
        where('phone', '==', phoneNumber)
      );
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0];
        return { id: doc.id, ...doc.data() };
      }
      return null;
    } catch (error) {
      console.error('Error finding client by phone:', error);
      throw new Error('Failed to find client: ' + error.message);
    }
  },

  // Find client by email
  async findClientByEmail(email) {
    try {
      const q = query(
        collection(db, CLIENTS_COLLECTION), 
        where('email', '==', email)
      );
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0];
        return { id: doc.id, ...doc.data() };
      }
      return null;
    } catch (error) {
      console.error('Error finding client by email:', error);
      throw new Error('Failed to find client: ' + error.message);
    }
  },

  // Delete a client
  async deleteClient(clientId) {
    try {
      await deleteDoc(doc(db, CLIENTS_COLLECTION, clientId));
      return true;
    } catch (error) {
      console.error('Error deleting client:', error);
      throw new Error('Failed to delete client: ' + error.message);
    }
  },

  // Listen to real-time client updates
  subscribeToClients(callback) {
    const q = query(collection(db, CLIENTS_COLLECTION), orderBy('createdAt', 'desc'));
    return onSnapshot(q, (querySnapshot) => {
      const clients = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      callback(clients);
    }, (error) => {
      console.error('Error in clients subscription:', error);
      callback(null, error);
    });
  }
};

// Product Services
export const productService = {
  // Create a new product
  async createProduct(productData) {
    try {
      const productWithTimestamp = {
        ...productData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      const docRef = await addDoc(collection(db, PRODUCTS_COLLECTION), productWithTimestamp);
      return { id: docRef.id, ...productWithTimestamp };
    } catch (error) {
      console.error('Error creating product:', error);
      throw new Error('Failed to create product: ' + error.message);
    }
  },

  // Update an existing product
  async updateProduct(productId, productData) {
    try {
      const productRef = doc(db, PRODUCTS_COLLECTION, productId);
      const updateData = {
        ...productData,
        updatedAt: serverTimestamp()
      };
      
      await updateDoc(productRef, updateData);
      return { id: productId, ...updateData };
    } catch (error) {
      console.error('Error updating product:', error);
      throw new Error('Failed to update product: ' + error.message);
    }
  },

  // Get all products
  async getAllProducts() {
    try {
      const querySnapshot = await getDocs(collection(db, PRODUCTS_COLLECTION));
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting products:', error);
      throw new Error('Failed to get products: ' + error.message);
    }
  },

  // Get a single product by ID
  async getProduct(productId) {
    try {
      const productRef = doc(db, PRODUCTS_COLLECTION, productId);
      const productSnap = await getDoc(productRef);
      
      if (productSnap.exists()) {
        return { id: productSnap.id, ...productSnap.data() };
      } else {
        throw new Error('Product not found');
      }
    } catch (error) {
      console.error('Error getting product:', error);
      throw new Error('Failed to get product: ' + error.message);
    }
  },

  // Delete a product
  async deleteProduct(productId) {
    try {
      // First get the product to check if it has an image
      const product = await this.getProduct(productId);
      
      // Delete the product document
      await deleteDoc(doc(db, PRODUCTS_COLLECTION, productId));
      
      // If the product had an image, try to delete it from storage
      if (product && product.image) {
        try {
          // Extract filename from URL if possible
          const urlParts = product.image.split('/');
          const fileName = urlParts[urlParts.length - 1].split('?')[0];
          if (fileName) {
            await storageService.deleteProductImage(`products/${fileName}`);
          }
        } catch (storageError) {
          console.warn('Could not delete product image from storage:', storageError);
          // Don't fail the product deletion if image deletion fails
        }
      }
      
      return true;
    } catch (error) {
      console.error('Error deleting product:', error);
      throw new Error('Failed to delete product: ' + error.message);
    }
  },

  // Listen to real-time product updates
  subscribeToProducts(callback) {
    const q = query(collection(db, PRODUCTS_COLLECTION), orderBy('name'));
    return onSnapshot(q, (querySnapshot) => {
      const products = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      callback(products);
    }, (error) => {
      console.error('Error in products subscription:', error);
      callback(null, error);
    });
  },

  // Create all BioEdge products at once
  async createAllBioEdgeProducts() {
    const products = [
      {
        type: 'Bio Edge',
        name: 'Semaglutide HD 10mg',
        price: 1800,
        costPrice: 900,
        courseDuration: 12,
        reorderWeeks: 10,
        courseDescription: '3-month weight loss program',
        image: 'https://firebasestorage.googleapis.com/v0/b/safaschools-a6f8c.appspot.com/o/WhatsApp_Image_2025-06-10_at_19.07.53_a6868ca1-removebg-preview.png?alt=media&token=2030aee1-91b9-40af-8821-185e54d3a540',
        description: 'Bio Edge offers Semaglutide 10mg, a highly effective solution for managing type 2 diabetes and aiding in weight loss.'
      },
      {
        type: 'Bio Edge',
        name: 'Tirzepatide BP 30mg',
        price: 3100,
        costPrice: 1550,
        courseDuration: 12,
        reorderWeeks: 10,
        courseDescription: '3-month body composition program',
        image: 'https://firebasestorage.googleapis.com/v0/b/eyemanager-590a1.appspot.com/o/images%2FWhatsApp_Image_2025-03-18_at_12.38.32_9ec9dabb-removebg-preview.png?alt=media&token=e29e6050-f0ce-4507-b7ba-f84ef8fdbe8e',
        description: 'Bio Edge offers Tirzepatide 30mg: A dual-action medication that improves blood sugar control and aids in weight loss by targeting both GLP-1 and GIP receptors.'
      },
      {
        type: 'Bio Edge',
        name: 'Retatrutide BP 32mg',
        price: 3800,
        costPrice: 1900,
        courseDuration: 12,
        reorderWeeks: 10,
        courseDescription: '3-month intensive program',
        image: 'https://firebasestorage.googleapis.com/v0/b/eyemanager-590a1.appspot.com/o/images%2FWhatsApp_Image_2025-03-18_at_12.38.33_f3c2d305-removebg-preview.png?alt=media&token=c7829d24-4f6a-4f69-894e-0a8390430081',
        description: 'Bio Edge offers Retatrutide 32mg: A promising treatment for obesity and metabolic disorders, enhancing fat loss and improving insulin sensitivity.'
      },
      {
        type: 'Bio Edge',
        name: 'Tesamorelin BP 5mg',
        price: 2600,
        costPrice: 1300,
        courseDuration: 12,
        reorderWeeks: 10,
        courseDescription: '3-month body composition program',
        image: 'https://firebasestorage.googleapis.com/v0/b/eyemanager-590a1.appspot.com/o/WhatsApp_Image_2025-03-18_at_12.38.35_49246bc4__1_-removebg-preview.png?alt=media&token=ebd4e750-a9a6-4c38-b67e-0948fb144450',
        description: 'Bio Edge offers Tesamorelin 5mg: A growth hormone-releasing hormone (GHRH) analogue, designed to reduce abdominal fat and improve metabolic health.'
      },
      {
        type: 'Bio Edge',
        name: 'Cagrisema BP 12mg',
        price: 2700,
        costPrice: 1350,
        courseDuration: 12,
        reorderWeeks: 10,
        courseDescription: '3-month weight management program',
        image: 'https://firebasestorage.googleapis.com/v0/b/eyemanager-590a1.appspot.com/o/images%2FWhatsApp_Image_2025-03-18_at_12.38.32_07fa2613-removebg-preview%20(1).png?alt=media&token=a8bb783e-c196-418a-89fa-6e626cacec5f',
        description: 'Bio Edge offers Cagrisema 12mg: A powerful weight loss treatment combining GLP-1 and GIP action to effectively manage body weight and support healthy blood sugar levels.'
      },
      {
        type: 'Bio Edge',
        name: 'Semaglutide BP 6mg',
        price: 2200,
        costPrice: 1100,
        courseDuration: 12,
        reorderWeeks: 10,
        courseDescription: '3-month diabetes management program',
        image: 'https://firebasestorage.googleapis.com/v0/b/eyemanager-590a1.appspot.com/o/images%2FWhatsApp_Image_2025-03-18_at_12.38.33_86a31405-removebg-preview.png?alt=media&token=6c6ab125-9fa9-47f9-8dc3-7db98a662f84',
        description: 'Bio Edge offers Semaglutide 6mg, a highly effective solution for managing type 2 diabetes and aiding in weight loss.'
      },
      {
        type: 'Bio Edge',
        name: 'Tirzepatide BP 60mg',
        price: 4600,
        costPrice: 2300,
        courseDuration: 12,
        reorderWeeks: 10,
        courseDescription: '3-month advanced weight loss program',
        image: 'https://firebasestorage.googleapis.com/v0/b/eezi-app-bc62e.appspot.com/o/WhatsApp_Image_2025-03-18_at_12.38.32_89d6d667-removebg-preview.png?alt=media&token=f6fd4b0c-cd1e-4913-ae47-e768c6e42ffe',
        description: 'Bio Edge offers Tirzepatide 60mg: A dual-action medication that improves blood sugar control and aids in weight loss by targeting both GLP-1 and GIP receptors.'
      },
      {
        type: 'Bio Edge',
        name: 'Retatrutide HD 32mg',
        price: 2300,
        costPrice: 1150,
        courseDuration: 12,
        reorderWeeks: 10,
        courseDescription: '3-month metabolic program',
        image: 'https://firebasestorage.googleapis.com/v0/b/movie-site-9a217.appspot.com/o/WhatsApp_Image_2025-07-09_at_10.38.59_f3902af6-removebg-preview.png?alt=media&token=901e3658-83e6-4eac-9b6f-3ab7643e2b8f',
        description: 'Bio Edge offers Retatrutide 32mg: A promising treatment for obesity and metabolic disorders, enhancing fat loss and improving insulin sensitivity.'
      },
      {
        type: 'Bio Edge',
        name: 'Tirzepatide HD 30mg',
        price: 2300,
        costPrice: 1150,
        courseDuration: 12,
        reorderWeeks: 10,
        courseDescription: '3-month advanced metabolic program',
        image: 'https://firebasestorage.googleapis.com/v0/b/safaschools-a6f8c.appspot.com/o/WhatsApp_Image_2025-06-10_at_19.07.53_4ebd711d-removebg-preview.png?alt=media&token=e737009a-d144-42e6-9702-8f0eee6876e3',
        description: 'Bio Edge offers Tirzepatide 30mg: A promising treatment for obesity and metabolic disorders, enhancing fat loss and improving insulin sensitivity.'
      }
    ];

    try {
      console.log('🌱 Starting to create all BioEdge products...');
      
      const batch = writeBatch(db);
      const results = [];
      let successCount = 0;
      let errorCount = 0;

      for (const product of products) {
        try {
          const docRef = doc(collection(db, PRODUCTS_COLLECTION));
          const productWithTimestamp = {
            ...product,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
          };
          
          batch.set(docRef, productWithTimestamp);
          results.push({ id: docRef.id, ...productWithTimestamp });
          successCount++;
          console.log(`✅ Prepared product: ${product.name}`);
        } catch (error) {
          console.error(`❌ Failed to prepare product ${product.name}:`, error.message);
          errorCount++;
        }
      }

      // Commit all products in a single batch
      await batch.commit();
      console.log(`🎉 Successfully created ${successCount} products in Firebase!`);
      
      return {
        success: true,
        created: successCount,
        errors: errorCount,
        products: results
      };
      
    } catch (error) {
      console.error('❌ Error creating products:', error);
      return {
        success: false,
        error: error.message,
        created: 0,
        errors: products.length
      };
    }
  }
};

// Order Services
export const orderService = {
  // Create a new order
  async createOrder(orderData) {
    try {
      const batch = writeBatch(db);
      
      // Generate unique order ID
      const orderId = generateOrderId();
      
      const orderWithTimestamp = {
        ...orderData,
        orderId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        status: orderData.status || 'pending'
      };
      
      // Add order to orders collection
      const orderRef = doc(collection(db, ORDERS_COLLECTION));
      batch.set(orderRef, orderWithTimestamp);
      
      // Update client's orders array and stats
      if (orderData.clientId) {
        const clientRef = doc(db, CLIENTS_COLLECTION, orderData.clientId);
        const clientSnap = await getDoc(clientRef);
        
        if (clientSnap.exists()) {
          const clientData = clientSnap.data();
          const updatedOrders = [...(clientData.orders || []), orderRef.id];
          const updatedTotalOrders = (clientData.totalOrders || 0) + 1;
          const updatedTotalSpent = (clientData.totalSpent || 0) + (orderData.totalAmount || 0);
          
          // Reset courseStartDate to the new order date - this restarts the progress cycle
          // This applies to ALL orders (new and existing clients) - resets to Week 0
          const orderDate = orderData.orderDate || new Date().toISOString().split('T')[0];
          
          batch.update(clientRef, {
            orders: updatedOrders,
            totalOrders: updatedTotalOrders,
            totalSpent: updatedTotalSpent,
            lastOrderDate: serverTimestamp(),
            courseStartDate: orderDate, // Reset to new order date - restarts progress cycle to Week 0
            updatedAt: serverTimestamp()
          });
        }
      }
      
      await batch.commit();
      return { id: orderRef.id, ...orderWithTimestamp };
    } catch (error) {
      console.error('Error creating order:', error);
      throw new Error('Failed to create order: ' + error.message);
    }
  },

  // Update order status
  async updateOrderStatus(orderId, status, additionalData = {}) {
    try {
      const orderRef = doc(db, ORDERS_COLLECTION, orderId);
      const updateData = {
        status,
        ...additionalData,
        updatedAt: serverTimestamp()
      };
      
      await updateDoc(orderRef, updateData);
      return { id: orderId, ...updateData };
    } catch (error) {
      console.error('Error updating order status:', error);
      throw new Error('Failed to update order status: ' + error.message);
    }
  },

  // Update an existing order
  async updateOrder(orderId, orderData) {
    try {
      const orderRef = doc(db, ORDERS_COLLECTION, orderId);
      const updateData = {
        ...orderData,
        updatedAt: serverTimestamp()
      };
      
      await updateDoc(orderRef, updateData);
      return { id: orderId, ...updateData };
    } catch (error) {
      console.error('Error updating order:', error);
      throw new Error('Failed to update order: ' + error.message);
    }
  },

  // Get all orders
  async getAllOrders() {
    try {
      const q = query(collection(db, ORDERS_COLLECTION), orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => {
        const data = doc.data();
        
        // Convert Firestore Timestamps to ISO strings if needed
        if (data.createdAt && data.createdAt.toDate) {
          data.createdAt = data.createdAt.toDate().toISOString();
        }
        if (data.updatedAt && data.updatedAt.toDate) {
          data.updatedAt = data.updatedAt.toDate().toISOString();
        }
        if (data.lastOrderDate && data.lastOrderDate.toDate) {
          data.lastOrderDate = data.lastOrderDate.toDate().toISOString().split('T')[0];
        }
        
        return {
          id: doc.id,
          ...data
        };
      });
    } catch (error) {
      console.error('Error getting orders:', error);
      throw new Error('Failed to get orders: ' + error.message);
    }
  },

  // Get orders by client ID
  async getOrdersByClient(clientId) {
    try {
      const q = query(
        collection(db, ORDERS_COLLECTION), 
        where('clientId', '==', clientId),
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => {
        const data = doc.data();
        
        // Convert Firestore Timestamps to ISO strings if needed
        if (data.createdAt && data.createdAt.toDate) {
          data.createdAt = data.createdAt.toDate().toISOString();
        }
        if (data.updatedAt && data.updatedAt.toDate) {
          data.updatedAt = data.updatedAt.toDate().toISOString();
        }
        if (data.lastOrderDate && data.lastOrderDate.toDate) {
          data.lastOrderDate = data.lastOrderDate.toDate().toISOString().split('T')[0];
        }
        
        return {
          id: doc.id,
          ...data
        };
      });
    } catch (error) {
      console.error('Error getting orders by client:', error);
      throw new Error('Failed to get client orders: ' + error.message);
    }
  },

  // Get a single order by ID
  async getOrder(orderId) {
    try {
      const orderRef = doc(db, ORDERS_COLLECTION, orderId);
      const orderSnap = await getDoc(orderRef);
      
      if (orderSnap.exists()) {
        return { id: orderSnap.id, ...orderSnap.data() };
      } else {
        throw new Error('Order not found');
      }
    } catch (error) {
      console.error('Error getting order:', error);
      throw new Error('Failed to get order: ' + error.message);
    }
  },

  // Delete an order
  async deleteOrder(orderId) {
    try {
      await deleteDoc(doc(db, ORDERS_COLLECTION, orderId));
      return true;
    } catch (error) {
      console.error('Error deleting order:', error);
      throw new Error('Failed to delete order: ' + error.message);
    }
  },

  // Listen to real-time order updates
  subscribeToOrders(callback) {
    const q = query(collection(db, ORDERS_COLLECTION), orderBy('createdAt', 'desc'));
    return onSnapshot(q, (querySnapshot) => {
      const orders = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      callback(orders);
    }, (error) => {
      console.error('Error in orders subscription:', error);
      callback(null, error);
    });
  },

  // Get orders due for reorder
  async getReorderDueOrders() {
    try {
      const today = new Date();
      
      // Validate today's date
      if (isNaN(today.getTime())) {
        throw new Error('Invalid current date');
      }
      
      const todayString = today.toISOString().split('T')[0];
      
      const q = query(
        collection(db, ORDERS_COLLECTION),
        where('nextReorderDate', '<=', todayString),
        orderBy('nextReorderDate', 'asc')
      );
      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => {
        const data = doc.data();
        
        // Convert Firestore Timestamps to ISO strings if needed
        if (data.createdAt && data.createdAt.toDate) {
          data.createdAt = data.createdAt.toDate().toISOString();
        }
        if (data.updatedAt && data.updatedAt.toDate) {
          data.updatedAt = data.updatedAt.toDate().toISOString();
        }
        if (data.lastOrderDate && data.lastOrderDate.toDate) {
          data.lastOrderDate = data.lastOrderDate.toDate().toISOString().split('T')[0];
        }
        
        return {
          id: doc.id,
          ...data
        };
      });
    } catch (error) {
      console.error('Error getting reorder due orders:', error);
      throw new Error('Failed to get reorder due orders: ' + error.message);
    }
  }
};

// Category Services
export const categoryService = {
  // Get all categories
  async getAllCategories() {
    try {
      const querySnapshot = await getDocs(collection(db, CATEGORIES_COLLECTION));
      const categories = querySnapshot.docs.map(doc => ({
        id: doc.id,
        name: doc.data().name,
        ...doc.data()
      }));
      // Sort by name
      return categories.sort((a, b) => a.name.localeCompare(b.name));
    } catch (error) {
      console.error('Error getting categories:', error);
      throw new Error('Failed to get categories: ' + error.message);
    }
  },

  // Create a new category
  async createCategory(categoryName) {
    try {
      // Check if category already exists
      const existingCategories = await this.getAllCategories();
      const exists = existingCategories.some(cat => 
        cat.name.toLowerCase() === categoryName.toLowerCase()
      );
      
      if (exists) {
        throw new Error('Category already exists');
      }

      const categoryData = {
        name: categoryName.trim(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      const docRef = await addDoc(collection(db, CATEGORIES_COLLECTION), categoryData);
      return { id: docRef.id, ...categoryData };
    } catch (error) {
      console.error('Error creating category:', error);
      throw new Error('Failed to create category: ' + error.message);
    }
  },

  // Delete a category
  async deleteCategory(categoryId) {
    try {
      await deleteDoc(doc(db, CATEGORIES_COLLECTION, categoryId));
      return true;
    } catch (error) {
      console.error('Error deleting category:', error);
      throw new Error('Failed to delete category: ' + error.message);
    }
  },

  // Initialize default categories if none exist
  async initializeDefaultCategories() {
    try {
      const existingCategories = await this.getAllCategories();
      if (existingCategories.length > 0) {
        return { success: true, message: 'Categories already exist' };
      }

      const defaultCategories = [
        'Weight Management',
        'Hormone Therapy',
        'Body Composition',
        'Metabolic Health',
        'Anti-Aging',
        'Supplements',
        'Injectable',
        'Oral Medication'
      ];

      const batch = writeBatch(db);
      defaultCategories.forEach(categoryName => {
        const docRef = doc(collection(db, CATEGORIES_COLLECTION));
        batch.set(docRef, {
          name: categoryName,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      });

      await batch.commit();
      return { success: true, message: 'Default categories created' };
    } catch (error) {
      console.error('Error initializing categories:', error);
      throw new Error('Failed to initialize categories: ' + error.message);
    }
  }
};

// Firebase Storage Services
export const storageService = {
  // Upload image to Firebase Storage
  async uploadProductImage(file, productName) {
    try {
      console.log('🖼️ Starting image upload for product:', productName);
      
      // Validate input parameters
      if (!file) {
        throw new Error('No file provided for upload');
      }
      
      if (!productName || productName.trim() === '') {
        throw new Error('Product name is required for image upload');
      }
      
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        throw new Error('Invalid file type. Only JPG, PNG, and WebP images are allowed');
      }
      
      // Validate file size (5MB limit)
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        throw new Error('File size too large. Maximum size is 5MB');
      }
      
      // Create a unique filename
      const timestamp = Date.now();
      const randomId = Math.random().toString(36).substring(2, 15);
      const fileExtension = file.name.split('.').pop().toLowerCase();
      const sanitizedName = productName.replace(/[^a-zA-Z0-9]/g, '_').substring(0, 50);
      const fileName = `products/${sanitizedName}_${timestamp}_${randomId}.${fileExtension}`;
      
      console.log('📁 Generated filename:', fileName);
      
      // Create storage reference
      const storageRef = ref(storage, fileName);
      
      // Upload the file with metadata
      const metadata = {
        contentType: file.type,
        customMetadata: {
          productName: productName,
          uploadedAt: new Date().toISOString(),
          originalName: file.name
        }
      };
      
      const snapshot = await uploadBytes(storageRef, file, metadata);
      console.log('✅ Image uploaded successfully to Firebase Storage:', fileName);
      console.log('📊 Upload metadata:', snapshot.metadata);
      
      // Get the download URL
      const downloadURL = await getDownloadURL(snapshot.ref);
      console.log('🔗 Download URL obtained:', downloadURL);
      
      // Validate the URL
      if (!downloadURL || typeof downloadURL !== 'string') {
        throw new Error('Invalid download URL received from Firebase Storage');
      }
      
      // Verify the URL is accessible
      try {
        const response = await fetch(downloadURL, { method: 'HEAD' });
        if (!response.ok) {
          throw new Error('Uploaded image URL is not accessible');
        }
        console.log('✅ Image URL verified and accessible');
      } catch (verifyError) {
        console.warn('⚠️ Could not verify image URL accessibility:', verifyError.message);
        // Don't fail the upload if verification fails
      }
      
      return {
        success: true,
        url: downloadURL,
        fileName: fileName,
        size: file.size,
        type: file.type,
        metadata: snapshot.metadata
      };
    } catch (error) {
      console.error('❌ Error uploading image:', error);
      throw new Error('Failed to upload image: ' + error.message);
    }
  },

  // Delete image from Firebase Storage
  async deleteProductImage(fileName) {
    try {
      if (!fileName) return { success: true };
      
      console.log('🗑️ Deleting image:', fileName);
      const storageRef = ref(storage, fileName);
      await deleteObject(storageRef);
      console.log('✅ Image deleted successfully');
      
      return { success: true };
    } catch (error) {
      console.error('❌ Error deleting image:', error);
      // Don't throw error for deletion failures as it's not critical
      return { success: false, error: error.message };
    }
  },

  // Get image URL from Firebase Storage
  async getImageURL(fileName) {
    try {
      if (!fileName) return null;
      
      const storageRef = ref(storage, fileName);
      const url = await getDownloadURL(storageRef);
      return url;
    } catch (error) {
      console.error('❌ Error getting image URL:', error);
      return null;
    }
  },

  // List all product images in Firebase Storage (for debugging)
  async listProductImages() {
    try {
      console.log('📋 Listing all product images in Firebase Storage...');
      
      // Note: Firebase Storage doesn't have a direct list method in the web SDK
      // This is a placeholder for future implementation
      // You would need to use the Admin SDK on the server side for full listing
      
      return {
        success: true,
        message: 'Product images listing requires server-side implementation',
        note: 'Use Firebase Console to view uploaded images in the products/ folder'
      };
    } catch (error) {
      console.error('❌ Error listing product images:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  // Verify image exists in Firebase Storage
  async verifyImageExists(fileName) {
    try {
      if (!fileName) return { exists: false, error: 'No filename provided' };
      
      const storageRef = ref(storage, fileName);
      const url = await getDownloadURL(storageRef);
      
      // Try to fetch the image to verify it exists
      const response = await fetch(url, { method: 'HEAD' });
      
      return {
        exists: response.ok,
        url: url,
        status: response.status
      };
    } catch (error) {
      console.error('❌ Error verifying image:', error);
      return {
        exists: false,
        error: error.message
      };
    }
  }
};

// Validation utilities
export const validateClientData = (clientData) => {
  const errors = [];
  
  if (!clientData.firstName?.trim()) {
    errors.push('First name is required');
  }
  
  if (!clientData.lastName?.trim()) {
    errors.push('Last name is required');
  }
  
  if (!clientData.email?.trim()) {
    errors.push('Email is required');
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(clientData.email)) {
    errors.push('Invalid email format');
  }
  
  if (!clientData.phone?.trim()) {
    errors.push('Phone number is required');
  }
  
  if (!clientData.age || clientData.age < 1 || clientData.age > 120) {
    errors.push('Valid age is required');
  }
  
  return errors;
};

export const validateProductData = (productData) => {
  const errors = [];
  
  if (!productData.name?.trim()) {
    errors.push('Product name is required');
  }
  
  if (!productData.price || productData.price <= 0) {
    errors.push('Valid price is required');
  }
  
  if (!productData.costPrice || productData.costPrice <= 0) {
    errors.push('Valid cost price is required');
  }
  
  if (!productData.courseDuration || productData.courseDuration <= 0) {
    errors.push('Valid course duration is required');
  }
  
  if (!productData.reorderWeeks || productData.reorderWeeks <= 0) {
    errors.push('Valid reorder weeks is required');
  }
  
  return errors;
};

export const validateOrderData = (orderData) => {
  const errors = [];
  
  if (!orderData.clientId?.trim()) {
    errors.push('Client selection is required');
  }
  
  if (!orderData.productId?.trim()) {
    errors.push('Product selection is required');
  }
  
  if (!orderData.totalAmount || orderData.totalAmount <= 0) {
    errors.push('Valid total amount is required');
  }
  
  return errors;
};